// File: Wip.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Button, Container, LoadingScreen, ModalOverlay, Page } from '@punchcard/core';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconDoubleArrowLeft, IconBookLeft, IconBookRight } from '@punchcard/core/icons';

import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import Resources from 'components/wip/Resources';
import classNames from 'classnames';
import DisplayPDF from './DisplayPDF';
import documentAPI from 'api/documentsAPI';
import { FormProvider, useForm } from 'react-hook-form';
import educatorsApi from 'api/educatorApi';
import { addClass, containsClass, removeClass, toggleClass } from '@punchcard/core/utils';
import { ASSESSMENT_STATUS } from '@punchcard/core/constants/statusConstants';

interface IGradeModal {
	title: string;
	body: string;
}
function Wip() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { courseId, unitId, studentId, activityId, assessmentId, attemptNumber } = useParams();
	const [course, setCourse] = useState<TeacherCourseDTO>();
	const [loading, setLoading] = useState<boolean>(true);
	const [activeUnit, setActiveUnit] = React.useState<number>(0);
	const [review, setReview] = React.useState<boolean>(false);
	const [showWarningModal, setShowWarningModal] = React.useState<IGradeModal | undefined>(undefined);
	const [showDiscardModal, setShowDiscardModal] = React.useState<boolean>(false);
	const [isWorkbookCompletion, setIsWorkbookCompletion] = React.useState<boolean>(false);
	const [currentActivity, setCurrentActivity] = useState<currentActivityAssessment | undefined>();
	const [activeTab, setActiveTab] = useState<number>(0);
	const pageRef = useRef<HTMLDivElement>(null);
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
	const [isBlockerEnabled, setIsBlockerEnabled] = useState(true);
	const safeStatuses = ['InProgress', 'Open', 'ReAttempt', ''];
	const isActivityOpen = (currentActivity && (currentActivity?.item?.activityStatus == null || safeStatuses.includes(currentActivity?.item?.activityStatus.statusCode))) ?? true;
	const wipForm = useForm<WIPForm>({
		defaultValues: {
			undoHistory: [],
			undoIndex: null,
			lastAction: null,
			completionGradeValue: course?.courseUnits[activeUnit].completionGradeValue,
		},
	});
	const { isDirty } = wipForm.formState;

	async function fetchCourse(courseId: string, studentId: string) {
		try {
			setLoading(true);
			const documents = await documentAPI.getStudentCourseUnit(courseId, studentId);
			if (documents === undefined) { navigate('/404'); }
			setCourse(documents);
			setActiveUnit(documents.courseUnits.findIndex((unit) => unit.id === parseInt(unitId ?? '')));
		} catch (error) {
			console.error('error fetching courses', error);
		}
		setLoading(false);
	}

	const toggleRightSidePanel = (type: 'add' | 'remove') => {
		if (pageRef) {
			if (type === 'add') {
				addClass(pageRef, 'active-right');
			} else {
				removeClass(pageRef, 'active-right');
			}
		}
	};

	useEffect(() => {
		if (courseId && studentId) {
			fetchCourse(courseId, studentId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courseId, studentId]);

	// Handle browser refresh/close with beforeunload
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isDirty) {
				const activeElement = document.activeElement as HTMLElement;

				if (activeElement.id === 'download-work-sample-button') {
					return; // Allow default behavior for download links
				}
				event.preventDefault();
				event.returnValue = ''; // Required for some browsers
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isDirty]);

	// Handle in-app navigation blocking
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) => {
			const activeElement = document.activeElement as HTMLElement;

			// Check if this is unit tab navigation
			const isUnitTabClick = activeElement?.id?.startsWith('unit-tab-');

			// Allow navigation if form is clean, or if clicking unit tabs, or if blocker is disabled
			if (!isDirty || isUnitTabClick || !isBlockerEnabled) {
				return false;
			}

			// Block navigation if paths are different
			if (currentLocation.pathname !== nextLocation.pathname) {
				setShowDiscardModal(true);
				setPendingAction(() => () => navigate(nextLocation.pathname));
				return true;
			}

			return false;
		}
	);

	React.useEffect(() => {
		if (!isBlockerEnabled && blocker.state === 'blocked') {
			blocker.proceed?.(); // Proceed with navigation
			setIsBlockerEnabled(true);
		}
	}, [blocker, isBlockerEnabled]);

	// On initial load, select activity or assessment if present in URL
	useEffect(() => {
		if (!course) {
			return;
		}
		let found: currentActivityAssessment | undefined;
		const unitIdx = course.courseUnits.findIndex((unit) => unit.id === parseInt(unitId ?? ''));
		if (unitIdx !== -1) {
			const unit = course.courseUnits[unitIdx];
			if (activityId) {
				for (const lesson of unit.courseLessons ?? []) {
					const activity = lesson.courseActivities?.find(a => a.id === parseInt(activityId));
					if (activity) {
						found = { item: activity, type: 'Activity' };
						break;
					}
				}
			}
			// Only select assessment if attemptNumber is provided
			// If assessmentId is present but no attemptNumber, behave like unit route (show summary)
			if (assessmentId && attemptNumber) {
				const assessment = unit.courseAssessments?.find(a => a.id === parseInt(assessmentId));
				if (assessment) {
					// Create a copy of the assessment and set reAttemptNumber
					const assessmentWithAttempt = { ...assessment, reAttemptNumber: parseInt(attemptNumber) };
					found = { item: assessmentWithAttempt, type: 'Assessment' };
				}
			}
		}
		if (found) {
			setCurrentActivity(found);
			toggleRightSidePanel('add');
		}
	// Only run when course, activityId, assessmentId, or attemptNumber changes
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [course, activityId, assessmentId, attemptNumber]);

	if (loading) {
		return <LoadingScreen />;
	}
	const handleWorkbookClick = () => {
		wipForm.setValue('completionGradeValue', course?.courseUnits[activeUnit].completionGradeValue);
		wipForm.setValue('gradeReceived', course?.courseUnits[activeUnit].gradeReceived);
		setIsWorkbookCompletion(true);
		setCurrentActivity(undefined);
	};
	const handleWorkbookSave = async () => {
		const grade = wipForm.getValues('gradeReceived');
		await documentAPI.postWorkbookCompletionGrade(studentId, courseId, unitId, grade);
		if (courseId && studentId) {
			fetchCourse(courseId, studentId);
		}
	};
	const handleWorkSampleToEDI = async () => {
		const id: number = await educatorsApi.educators.worksamplesendtoedi(studentId, unitId);
		if (id && course) {
			const today = new Date().toISOString();
			const updatedCourse: TeacherCourseDTO = {
				...course,
				courseUnits: course.courseUnits.map((unit, index) =>
					index === activeUnit
						? { ...unit, sendWorkSampleToEDI: today }	// set the date so the button to send the work sample is disabled
						: unit
				),
			};
			setCourse(updatedCourse);
		}
	};
	const handleGradeSave = () => {
		const { gradeReceived, comment, reason, approval } = wipForm.formState.dirtyFields;
		const grade = wipForm.getValues();

		// Helper function to show warning modal
		const showWarning = (titleKey: string, bodyKey: string) => {
			setShowWarningModal({
				title: titleKey,
				body: bodyKey
			});
		};

		// Check for specific warning conditions
		if (approval === true && grade.approval === false) {
			showWarning(t('exam.returned_changed'), t('exam.returned_changed_message'));
			return;
		}

		if (gradeReceived === true) {
			showWarning(t('exam.override_grade'), t('exam.override_grade_message'));
			return;
		}

		if ((approval === true && grade.approval === true) || (comment === true || reason === true)) {
			showWarning(t('exam.approval_or_comment'), t('exam.approval_or_comment_message'));
			return;
		}

		// Handle re-attempt logic
		if (isReAttemptAssessment()) {
			setReview(false);
		} else {
			handleSave();
		}
	};

	// Helper function to check if current activity is a re-attempt
	const isReAttemptAssessment = (): boolean => {
		if (!currentActivity?.item) { return false; }

		// Type guard to check if item has assessment attempts
		const hasAssessmentAttempts = (
			item: TeacherCourseActivityDTO | StudentCourseActivityDTO
		): item is TeacherCourseActivityDTO => {
			return (
				'assessmentAttempts' in item &&
				Array.isArray((item as TeacherCourseActivityDTO).assessmentAttempts) &&
				'reAttemptNumber' in item
			);
		};

		if (!hasAssessmentAttempts(currentActivity.item)) { return false; }

		const { assessmentAttempts, reAttemptNumber } = currentActivity.item;

		if (!reAttemptNumber || !assessmentAttempts?.length) { return false; }

		const currentAttempt = assessmentAttempts.find(
			attempt => attempt.attemptNumber === reAttemptNumber
		);

		return currentAttempt?.statusCode === ASSESSMENT_STATUS.RE_ATTEMPT;
	};

	const handleSave = async () => {
		const data: WIPForm = wipForm.getValues();
		if (studentId && currentActivity?.item?.id) {
			const dto: TeacherCourseExamStudentAnswerDTO = {
				...data,
				studentId: parseInt(studentId),
				courseAssessmentId: currentActivity?.item?.id,
				examName: '',
				gradeReceived: data.gradeReceived ?? 0,
				comment: data.comment ?? '',
				reason: data.reason ?? '',
				approval: data.approval,
			};
			documentAPI.postStudentExamGrade(dto)
				.then(() => {
					setReview(false);
					setActiveTab(0);
					if (courseId && studentId) {
						fetchCourse(courseId, studentId);
					}
				});
		}
	};

	// Update URL on in-app navigation to activity/assessment
	const handleActivityChange = (item: currentActivityAssessment) => {
		setCurrentActivity(item);
		toggleRightSidePanel('add');

		// Temporarily disable blocker for programmatic navigation
		setIsBlockerEnabled(false);

		if (item.type === 'Activity') {
			navigate(`/students/${studentId}/course/${courseId}/unit/${unitId}/activity/${item.item.id}`, { replace: true });
		} else if (item.type === 'Assessment') {
			// Type guard to check if item has reAttemptNumber property
			const hasReAttemptNumber = (obj: unknown): obj is { reAttemptNumber: number } => {
				return typeof obj === 'object' && obj !== null && 'reAttemptNumber' in obj;
			};

			// Only navigate to assessment URLs if there's a specific attempt number
			// Otherwise, redirect to unit summary (assessments without attempts should show unit overview)
			if (hasReAttemptNumber(item.item) && item.item.reAttemptNumber) {
				const assessmentUrl = `/students/${studentId}/course/${courseId}/unit/${unitId}/assessment/${item.item.id}/attempt/${item.item.reAttemptNumber}`;
				navigate(assessmentUrl, { replace: true });
			} else {
				// Redirect to unit summary when accessing assessment without specific attempt
				navigate(`/students/${studentId}/course/${courseId}/unit/${unitId}`, { replace: true });
			}
		}

		// Re-enable blocker after navigation
		setTimeout(() => {
			setIsBlockerEnabled(true);
		}, 0);

		return true;
	};
	async function onStatusChange(courseActivityId: number | undefined, status: string, type: string | undefined, gradeReceived: number | undefined) {
		if (studentId && courseActivityId && status) {
			await documentAPI.postactivitystatus(courseActivityId, studentId, status, type, gradeReceived);
			fetchCourse(courseId ?? '', studentId);
		}
	}

	const handleBack = () => {
		if (isDirty) {
			setPendingAction(() => () => navigate(`/students/${studentId}/courses/${courseId}/units?tab=1`));
			setShowDiscardModal(true);
			return;
		}
		navigate(`/students/${studentId}/courses/${courseId}/units?tab=1`);
	};

	const confirmDiscardChanges = () => {
		// Reset form state first
		wipForm.reset();
		setReview(false);
		setShowDiscardModal(false);
		setIsBlockerEnabled(false);

		// Handle navigation based on the current state
		setTimeout(() => {
			if (pendingAction) {
				pendingAction();
				setPendingAction(null);
			} else if (blocker.state === 'blocked') {
				blocker.proceed?.();
			}
		}, 0);
	};

	return (
		<FormProvider {...wipForm}>
			<Page ref={pageRef} className="bg-light flex-column scroll-container-wrapper wip" scrollable={false}>
				<header className="px-3 py-2 d-flex align-items-center justify-content-between">
					<div className="d-flex align-items-center">
						<div className="pe-3 me-3 border-end">
							<Button className="btn-ghost-primary" icon={<IconArrowLeft />} onClick={handleBack}>{t('back')}</Button>
						</div>
						<h6>
							{course?.courseName ? course?.courseName : t('wip.course')}
						</h6>
					</div>
					{currentActivity?.item.assessmentType && currentActivity?.item.assessmentType.length > 0
						&& <Button onClick={wipForm.handleSubmit(handleGradeSave)} disabled={!review}>{t('exam.save_and_exit')}</Button>}
				</header>
				<div className="layout-toggle">
					<Button
						className={classNames('mode-toggle-button btn-collapse')}
						onClick={() => toggleClass(pageRef, 'single-mode')}
						aria-expanded={containsClass(pageRef, 'single-mode')}
						aria-controls="wip-container-wrapper"
						aria-label={containsClass(pageRef, 'single-mode') ? t('wip.single_view') : t('wip.dual_view')}
					>
						<IconDoubleArrowLeft className="icon-27 iconArrow" />
					</Button>
					<div className="side-select-buttons">
						<Button
							className="left-panel-button btn-collapse"
							onClick={() => toggleRightSidePanel('remove')}
							aria-expanded={containsClass(pageRef, 'active-right')}
							aria-controls="left-panel"
						>
							<IconBookLeft className="icon-27" />
						</Button>
						<Button
							className="right-panel-button btn-collapse"
							onClick={() => toggleRightSidePanel('add')}
							aria-expanded={!containsClass(pageRef, 'active-right')}
							aria-controls="right-panel"
						>
							<IconBookRight className="icon-27" />
						</Button>
					</div>
				</div>
				<Container id="wip-container-wrapper" className="wip-container-wrapper">
					<div id="left-panel" className="left-panel">
						<Resources
							studentId={studentId}
							studentName={course?.studentName}
							course={course}
							courseId={courseId}
							setCurrentActivity={(activity) => handleActivityChange(activity)}
							safeStatuses={safeStatuses}
							activeUnit={activeUnit}
							setActiveUnit={setActiveUnit}
							examReview={review}
							setExamReview={setReview}
							currentActivityItem={currentActivity?.item as TeacherCourseActivityDTO}
							activeTab={activeTab}
							setActiveTab={setActiveTab}
							handleWorkbookClick={handleWorkbookClick}
							handleWorkSampleToEDI={handleWorkSampleToEDI}
							setPendingAction={setPendingAction}
							setShowDiscardModal={setShowDiscardModal}
						/>
					</div>
					<div id="right-panel" className="right-panel">
						<DisplayPDF
							studentId={studentId}
							submitDrawing={() => false}
							currentActivity={currentActivity}
							setCurrentActivity={setCurrentActivity}
							onStatusChange={onStatusChange}
							isActivityOpen={isActivityOpen}
							review={review}
							setReview={setReview}
							isWorkbookCompletion={isWorkbookCompletion}
							handleWorkbookSave={handleWorkbookSave}
						/>
					</div>
					<ModalOverlay
						isOpen={showWarningModal !== undefined}
						modalSize="md"
						onRequestClose={() => setShowWarningModal(undefined)}
						headerChildren={showWarningModal?.title}
						confirmButtonChildren={t('confirm')}
						cancelButtonChildren={t('cancel')}
						hideCloseButton={true}
						confirmButtonAction={() => {
							setShowWarningModal(undefined);
							handleSave();
						}}
						cancelButtonAction={() => {
							setShowWarningModal(undefined);
						}}
					>
						{showWarningModal && showWarningModal.body}
					</ModalOverlay>
					<ModalOverlay
						isOpen={showDiscardModal || blocker.state === 'blocked'}
						modalSize="md"
						onRequestClose={() => setShowDiscardModal(false)}
						headerChildren={t('exam.discard_changes')}
						confirmButtonChildren={t('confirm')}
						cancelButtonChildren={t('cancel')}
						hideCloseButton={true}
						confirmButtonAction={confirmDiscardChanges}
						cancelButtonAction={() => {
							blocker.reset?.();
							setShowDiscardModal(false);
						}}
					>
						{t('exam.discard_changes_message')}
					</ModalOverlay>

				</Container>
			</Page>
		</FormProvider>
	);
}

export default Wip;
